import { useEffect, useRef } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { isNativeShell } from '@mlm/ui/lib/native-shell';
import { CoachButton, Eyebrow } from '../components/primitives';
import { coaching, fmtPeso, type CoachingPlan } from '../lib/api';
import { qk, useEntitlement, useProfile, useTierLabels } from '../lib/queries';

/**
 * SC-11, the one moment the app asks for money: after the last onboarding
 * question and before the blueprint is written.
 *
 * It is shown ONCE, it never blocks, and "Continue with Free" is a real answer
 * — a member who says no still gets their plan written on the next screen. If
 * the tenant has no plan on sale the screen is skipped entirely rather than
 * showing a price nobody can pay.
 *
 * Since the 2026-10-13 meeting the app sells two tiers (Standard and Premium
 * at Miracles Protocol), so this quotes one plan per tier, named as the
 * tenant named it, with the lines each adds read from the same feature table
 * the gates enforce.
 */

/** What the first paid plan adds, for an API build that does not send the lines itself. */
const INCLUDED = [
  'A coach who knows your goal, with more messages each day',
  'Actions suggested for the goal you are actually working on',
  'Journal reflections and affirmations written for you',
  'More than one goal at a time',
  'A streak freeze each month, for the day life gets in the way',
];

/** One plan per tier to quote: the monthly one, else whatever that tier sells. */
function offersOf(plans: CoachingPlan[] | undefined): CoachingPlan[] {
  const byTier = new Map<CoachingPlan['tier'], CoachingPlan>();
  for (const p of plans ?? []) {
    const have = byTier.get(p.tier);
    if (!have || (have.period !== 'monthly' && p.period === 'monthly')) byTier.set(p.tier, p);
  }
  return [...byTier.values()];
}

const periodWord = (period: CoachingPlan['period']) => (period === 'annual' ? 'a year' : 'a month');

export function PaywallPage() {
  const navigate = useNavigate();
  const profile = useProfile();
  const entitlement = useEntitlement();
  const labels = useTierLabels();
  const plans = useQuery({ queryKey: qk.plans, queryFn: coaching.plans, staleTime: 5 * 60_000 });
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    coaching.event('paywall_viewed');
  }, []);

  const onboarded = Boolean(profile.data?.profile.onboardingCompletedAt);
  // No free tier (`paid_only`): this screen is a DOOR, shown until a plan is
  // live, and again to a lapsed member. Otherwise, once onboarding is done,
  // it has no meaning any more.
  const walled = entitlement.data?.paidOnly === true && entitlement.data.tier === 'free';
  if (onboarded && !walled) return <Navigate to="/home" replace />;
  if (!entitlement.data) return null;
  const lapsed = onboarded && walled;

  // Inside the packaged app the subscription is bought on the website (Google
  // Play's billing rule), so the button opens miraclesprotocol.com in the
  // phone's browser; the app re-reads the plan when it comes back. Without the
  // wall there is nothing to sell in the app, so the member goes on to their
  // blueprint.
  if (isNativeShell() && !walled) return <Navigate to="/blueprint" replace />;
  const websiteUpgrade = `${window.location.origin.replace(/^https?:\/\/app\./, 'https://')}/upgrade`;

  const offers = offersOf(plans.data);
  // Nothing on sale, or the plans have not loaded: do not hold the member at a
  // wall with no door. They go straight on to their blueprint.
  if (!plans.isPending && offers.length === 0) return <Navigate to="/blueprint" replace />;
  const several = offers.length > 1;

  return (
    <div className="mx-auto flex min-h-screen w-full max-w-md flex-col px-5 pb-10 pt-8 lg:pt-16">
      <p className="font-display text-lg font-semibold text-ink">Miracles Protocol</p>
      <div className="flex flex-1 flex-col justify-center py-10">
        <Eyebrow className="mb-3">
          {several ? 'Choose your plan' : (offers[0]?.tierLabel ?? labels.pro)}
        </Eyebrow>
        <h1 className="font-display text-[2.1rem] font-semibold leading-[1.1] text-ink">
          Unlock your complete Success System
        </h1>
        <p className="mt-3 text-[0.95rem] leading-relaxed text-ink/70">
          Personal coaching, daily actions written around your goal, and the tools to keep the
          practice going.
        </p>
        <div className="mt-7 space-y-4">
          {offers.map((offer, i) => {
            const name = offer.tierLabel ?? labels[offer.tier];
            const lines = offer.included?.length ? offer.included : i === 0 ? INCLUDED : [];
            return (
              <section
                key={offer.id}
                className="border border-[rgba(16,32,26,0.22)] px-4 py-4"
                aria-label={name}
              >
                <div className="flex items-baseline justify-between gap-3">
                  <h2 className="font-display text-[1.25rem] font-semibold text-ink">{name}</h2>
                  <p className="font-display text-[1.25rem] font-semibold text-ink">
                    {fmtPeso(offer.price)}
                    <span className="ml-1.5 font-sans text-xs font-normal text-sage">
                      {periodWord(offer.period)}
                    </span>
                  </p>
                </div>
                {lines.length > 0 && (
                  <ul className="mt-3 space-y-2">
                    {lines.map((line) => (
                      <li key={line} className="flex items-start gap-3 text-[0.9rem] text-ink/85">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" aria-hidden />
                        {line}
                      </li>
                    ))}
                  </ul>
                )}
                {!isNativeShell() && (
                  <div className="mt-4">
                    <CoachButton
                      kind={i === offers.length - 1 ? 'gold' : 'quiet'}
                      block
                      onClick={() => navigate(`/upgrade?plan=${offer.id}&from=paywall`)}
                    >
                      {lapsed ? `Renew ${name}` : `Continue with ${name}`}
                    </CoachButton>
                  </div>
                )}
              </section>
            );
          })}
        </div>
      </div>
      <div className="space-y-3">
        {isNativeShell() && (
          <a href={websiteUpgrade} target="_blank" rel="noopener" className="block">
            <CoachButton kind="gold" block>
              Subscribe at miraclesprotocol.com
            </CoachButton>
          </a>
        )}
        {!walled && (
          <CoachButton kind="quiet" block onClick={() => navigate('/blueprint', { replace: true })}>
            Continue with Free
          </CoachButton>
        )}
        <p className="eyebrow text-center">
          {isNativeShell()
            ? 'Your plan opens here as soon as it is paid on the website.'
            : 'Each month is paid on its own. Nothing renews by itself.'}
        </p>
      </div>
    </div>
  );
}
